import React, { lazy } from 'react';
import { Layers, GitMerge, ShieldCheck, Building2, ArrowRight, X, Check } from 'lucide-react';
import { Eyebrow } from '../ui/Section';
import { Button } from '../ui/Button';
import { IMAGES } from '../../lib/constants';
const benefits = [{
  icon: Layers,
  title: 'Direct capability',
  body: 'Fewer layers between your firm and the people responsible for the environment.'
}, {
  icon: GitMerge,
  title: 'Clear ownership',
  body: 'One partner can coordinate hosting, support, security, software and voice.'
}, {
  icon: ShieldCheck,
  title: 'Continuity by design',
  body: 'Backup, recovery and operational dependencies can be considered as one system.'
}, {
  icon: Building2,
  title: 'Built around the firm',
  body: 'Hosting and support can be aligned to practice applications, users, locations and risk.'
}];
const reseller = ['More handoffs', 'Separate suppliers', 'Less direct control', 'Responsibility can be fragmented'];
const ctModel = ['Direct infrastructure capability', 'One responsibility map', 'Integrated services', 'Clear escalation path'];
export function Infrastructure() {
  return <section id="difference" className="relative w-full overflow-hidden bg-navy" aria-label="Infrastructure advantage" data-id="element-273">
      <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-274">
        {/* image side */}
        <div className="relative min-h-[280px] lg:min-h-full" data-id="element-275">
          <img src={IMAGES.datacentre} alt="A clean CT Group data-centre aisle with rows of server racks" loading="lazy" className="absolute inset-0 h-full w-full object-cover" data-id="element-276" />
          <div className="absolute inset-0" style={{
          background: 'linear-gradient(90deg, rgba(8,27,47,0.25), rgba(8,27,47,0.85))'
        }} aria-hidden data-id="element-277" />
        </div>

        {/* content side */}
        <div className="px-5 py-16 md:px-10 lg:py-24 lg:pr-12" data-id="element-278">
          <div className="mx-auto max-w-xl lg:mx-0" data-id="element-279">
            <Eyebrow onDark color="var(--ct-light-blue)" data-id="element-280">
              A genuine infrastructure advantage
            </Eyebrow>
            <h2 className="mt-4 font-display text-[30px] font-black leading-tight text-white md:text-[42px]" data-id="element-281">
              We do not simply resell someone else’s platform.
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-white/75 md:text-[18px]" data-id="element-282">
              CT Group builds and operates private cloud, server and data-centre
              infrastructure. That gives clients direct capability, clearer
              accountability and an environment designed around the way their
              firm works.
            </p>

            <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2" data-id="element-283">
              {benefits.map(b => <div key={b.title} data-id="element-284">
                  <b.icon className="h-6 w-6 text-ct-cyan" aria-hidden data-id="element-285" />
                  <h3 className="mt-3 font-display text-[16px] font-bold text-white" data-id="element-286">
                    {b.title}
                  </h3>
                  <p className="mt-1.5 text-[14px] leading-relaxed text-white/65" data-id="element-287">
                    {b.body}
                  </p>
                </div>)}
            </div>

            {/* comparison */}
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-288">
              <div className="rounded-card border border-white/10 bg-white/[0.04] p-5" data-id="element-289">
                <p className="text-[13px] font-bold uppercase tracking-wide text-white/50" data-id="element-290">
                  Reseller-only model
                </p>
                <ul className="mt-3 space-y-2" data-id="element-291">
                  {reseller.map(r => <li key={r} className="flex items-center gap-2 text-[14px] text-white/70" data-id="element-292">
                      <X className="h-4 w-4 flex-shrink-0 text-security-soft" aria-hidden data-id="element-293" />
                      {r}
                    </li>)}
                </ul>
              </div>
              <div className="rounded-card border border-ct-cyan/30 bg-ct-cyan/[0.08] p-5" data-id="element-294">
                <p className="text-[13px] font-bold uppercase tracking-wide text-ct-light" data-id="element-295">
                  CT Group model
                </p>
                <ul className="mt-3 space-y-2" data-id="element-296">
                  {ctModel.map(r => <li key={r} className="flex items-center gap-2 text-[14px] text-white" data-id="element-297">
                      <Check className="h-4 w-4 flex-shrink-0 text-managed-pale" aria-hidden data-id="element-298" />
                      {r}
                    </li>)}
                </ul>
              </div>
            </div>

            <div className="mt-8" data-id="element-299">
              <Button as="a" href="#it-review" variant="primary" data-id="element-300">
                See how our infrastructure model works
                <ArrowRight className="h-4 w-4" aria-hidden data-id="element-301" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}